import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { DocxEditor, type DocxEditorRef } from "@eigenpal/docx-editor-react";
import {
  acceptAllChanges,
  acceptChange,
  findNextChange,
  rejectAllChanges,
  rejectChange,
} from "@eigenpal/docx-editor-core/prosemirror/commands";
import { Button } from "../primitives/button";
import type { EditSession } from "./EditSession";
import { fetchSessionBytes } from "./fetchSessionBytes";
import { type AIProposal, saveDraft } from "./persistence";

/**
 * Optional "open externally" handoff. When provided, a small chip
 * button appears next to the Salva CTA inside Eigenpal's TitleBar.
 * The caller is responsible for the actual shell.open() — this prop is
 * just the trigger glue (May 25, 2026 — moved here from a standalone
 * ArtifactToolbar band so the chrome stack collapses to a single row).
 */
export interface DocxOpenExternalHandoff {
  readonly label: string;
  readonly onClick: () => void;
  readonly busy?: boolean;
}

/**
 * Public ref surface — the chat HITL bridge holds one of these per editor
 * to push tracked-change suggestions imperatively. We do NOT expose the
 * underlying ProseMirror view: the editor primitive owns its internals
 * and offers a small action-shaped API so the bridge stays mime-agnostic.
 */
/**
 * A single tracked change extracted from the Eigenpal/ProseMirror state.
 * Surfaced via `listTrackedChanges()` so consumers (review panel, AI
 * summary widget) can render an accept/reject UI without poking the PM
 * internals themselves.
 */
export interface DocxTrackedChange {
  /** PM document position — the START of the change span. */
  readonly from: number;
  /** PM document position — the END of the change span. */
  readonly to: number;
  /** Insertion (new text appears) or deletion (existing text struck out). */
  readonly type: "insertion" | "deletion";
  /** The affected text (already rendered, max ~200 chars). */
  readonly text: string;
  /** Word `w14:paraId` of the containing paragraph — for `scrollToParaId`. */
  readonly paraId: string | null;
  /** Author of the change, if the PM mark carries one. */
  readonly author: string | null;
  /** ISO timestamp, if the PM mark carries one. */
  readonly timestamp: string | null;
}

export interface DocxEditorInlineHandle {
  /**
   * Apply an AI-proposed mutation as a *tracked change* (suggesting mode),
   * never as a direct edit — the user must explicitly accept before the
   * mutation becomes part of the saved doc.
   *
   * @returns `true` if the change was queued, `false` if the proposal kind
   *          is unsupported or the underlying editor isn't ready yet.
   */
  applyAIProposal: (proposal: AIProposal) => boolean;
  /** Snapshot the in-memory doc and persist it as a draft + invoke onSave. */
  flush: () => Promise<void>;
  /**
   * Walk the live PM doc and return every tracked-change span (insertions +
   * deletions). Returns `[]` when the editor isn't ready yet. The review
   * panel calls this synchronously after every `subscribeChanges` tick.
   */
  listTrackedChanges: () => readonly DocxTrackedChange[];
  /** Accept a single tracked change at the given PM range. */
  acceptChangeAt: (from: number, to: number) => boolean;
  /** Reject a single tracked change at the given PM range. */
  rejectChangeAt: (from: number, to: number) => boolean;
  /** Accept every tracked change in the doc. */
  acceptAllChanges: () => boolean;
  /** Reject every tracked change in the doc. */
  rejectAllChanges: () => boolean;
  /** Scroll the visible pages so the given PM position is in view. */
  scrollToPosition: (pos: number) => void;
  /** Scroll the visible pages so the given Word `w14:paraId` is in view. */
  scrollToParaId: (paraId: string) => boolean;
  /**
   * Locate `query` in the LIVE document — body, table cells AND text boxes —
   * returning one `{paraId, match}` per matching paragraph. Used to anchor a
   * BE-diff change to a real paragraph when the diff payload's paraId is
   * missing (common for table/text-box fields). Empty `[]` when not ready.
   */
  findInDoc: (
    query: string,
  ) => ReadonlyArray<{ paraId: string; match: string }>;
  /**
   * Subscribe to doc-content changes (any committed edit). Returns an
   * unsubscribe function. Used by the review panel to refresh its derived
   * tracked-change list without polling.
   */
  subscribeChanges: (listener: () => void) => () => void;
}

export interface DocxEditorInlineProps {
  session: EditSession;
  /**
   * Upstream save handler. The caller decides whether the blob goes to
   * MinIO via presigned PUT, S3 via STS, Drive REST, etc. — this primitive
   * stays transport-agnostic.
   *
   * Promise resolution clears `dirty_since`; rejection keeps the local
   * draft so the user can retry.
   */
  onSave?: (blob: Blob) => Promise<void>;
  /**
   * Optional "Apri in Word" / "Apri esternamente" handoff. When passed,
   * a small button appears in Eigenpal's TitleBar next to Salva. Keeps
   * the chrome stack thin (no separate toolbar band).
   */
  openExternal?: DocxOpenExternalHandoff;
  /**
   * Read-only preview (Jun 22, 2026). When true the editor renders but blocks
   * direct typing/saving — used to preview a native-OOXML-revision REDLINE blob
   * (``doc_diff_payload.redline_url``). Accept/reject still flow through the
   * review rail (programmatic ``acceptAllChanges``); the real apply runs
   * server-side on Confirm, so the revisioned blob is NEVER saved back.
   */
  readOnly?: boolean;
}

/** ms of inactivity before we snapshot to IndexedDB. */
const DRAFT_DEBOUNCE_MS = 800;

/**
 * Scoped CSS for the Eigenpal editor shell. Three responsibilities:
 *  1. Hide Eigenpal's MenuBar (File/Format/Insert/Help) — duplicates the
 *     outer FocusedViewLayout breadcrumb + Cmd+K palette is the canonical
 *     entry point for editor actions.
 *  2. Hide Eigenpal's documentName slot — the filename already lives in
 *     the outer rich-header (<h1>); having it twice was the May 26, 2026
 *     "doppio titolo" feedback.
 *  3. Apply ``cursor: text`` to the gutter around the rendered pages so
 *     the surface communicates editability — ProseMirror's content area
 *     keeps its own cursor by default.
 *
 * Defined with ``String.raw`` so the CSS escapes for Tailwind's bracketed
 * arbitrary-value classes (``\[100px\]``) survive without TypeScript
 * misreading the backslash sequence in a regular template literal.
 */
const INLINE_STYLES = String.raw`
  .docx-editor-inline-shell [role="menubar"] {
    display: none !important;
  }
  .docx-editor-inline-shell input.min-w-\[100px\].max-w-\[300px\].truncate,
  .docx-editor-inline-shell span.min-w-\[100px\].max-w-\[300px\].truncate {
    display: none !important;
  }
  .docx-editor-inline-shell {
    cursor: text;
  }
  /* The host review rail owns change cards. Eigenpal otherwise reserves a
     second 340px sidebar, forcing a 1360px canvas even in narrow panes. */
  .docx-editor-inline-preview .docx-unified-sidebar {
    display: none !important;
  }
  .docx-editor-inline-preview div:has(> div > .paged-editor) {
    min-width: 0 !important;
  }
  .docx-editor-inline-preview .paged-editor > div:has(> .paged-editor__pages) {
    transform: scale(var(--ando-docx-review-zoom, 1)) !important;
  }
`;

/**
 * DocxEditorInline — wraps the upstream `@eigenpal/docx-editor-react` editor
 * with Ando's local-first persistence story + a typed AI-proposal hook so
 * the chat side can route tracked-change suggestions into the open doc.
 *
 * Why a forwardRef: the chat HITL card lives outside this component but
 * needs to push proposals into it (e.g. "accept the rephrase"). An
 * imperative handle is the smallest API surface that doesn't leak the
 * ProseMirror schema upward.
 *
 * Local-first behaviour:
 *  - First mount fetches `session.read_url` once and feeds the bytes into
 *    the editor via the `documentBuffer` prop. Subsequent re-renders reuse
 *    the in-memory doc; we never refetch unless `session.session_id`
 *    changes.
 *  - On every `onChange` we debounce by `DRAFT_DEBOUNCE_MS` and snapshot
 *    the current bytes into IndexedDB so a tab crash / lease expiry never
 *    loses typed text.
 *  - The "Salva" button is the only path that talks upstream — we never
 *    auto-flush, because save endpoints are presigned and one-shot.
 */
export const DocxEditorInline = forwardRef<
  DocxEditorInlineHandle,
  DocxEditorInlineProps
>(function DocxEditorInline({ session, onSave, openExternal, readOnly = false }, ref) {
  const { i18n } = useLingui();
  const editorRef = useRef<DocxEditorRef | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const [buffer, setBuffer] = useState<ArrayBuffer | null>(null);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // One-shot fetch of the original .docx bytes. Cancel via AbortController
  // so a fast session swap (chat closes the artefact) doesn't drop the new
  // buffer on top of the old one.
  useEffect(() => {
    const ctrl = new AbortController();
    setBuffer(null);
    setLoadError(null);
    fetchSessionBytes(session.read_url, { signal: ctrl.signal })
      .then(setBuffer)
      .catch((err: unknown) => {
        if ((err as { name?: string }).name === "AbortError") return;
        setLoadError(err instanceof Error ? err : new Error(String(err)));
      });
    return () => ctrl.abort();
  }, [session.read_url]);

  // Fit review previews using the editor's own zoom API so page layout,
  // selection overlays and scroll-to-change coordinates share one scale.
  // Observe this pane, rather than window resize: opening the review rail
  // changes the available document width without resizing the window.
  useEffect(() => {
    const shell = shellRef.current;
    if (!readOnly || !buffer || !shell) return;
    let frame = 0;
    const fit = () => {
      frame = 0;
      const pages = shell.querySelectorAll<HTMLElement>(".layout-page");
      const pageWidth = Math.max(0, ...Array.from(pages, (page) => page.offsetWidth));
      const api = editorRef.current;
      if (!api || !pageWidth || !shell.clientWidth) return;
      const zoom = Math.min(1, Math.max(0.25, (shell.clientWidth - 48) / pageWidth));
      shell.style.setProperty("--ando-docx-review-zoom", String(zoom));
      if (Math.abs(api.getZoom() - zoom) > 0.005) api.setZoom(zoom);
    };
    const scheduleFit = () => {
      if (!frame) frame = requestAnimationFrame(fit);
    };
    const resize = new ResizeObserver(scheduleFit);
    resize.observe(shell);
    // Pages arrive asynchronously after DOCX parsing and font measurement.
    const pages = new MutationObserver(scheduleFit);
    pages.observe(shell, { childList: true, subtree: true });
    scheduleFit();
    return () => {
      resize.disconnect();
      pages.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [readOnly, buffer]);

  /** Persist the live editor bytes to IDB. Always read from the editor ref
   *  (the source of truth) — `buffer` is only the boot snapshot. */
  const persistDraft = useCallback(async (): Promise<Blob | null> => {
    const api = editorRef.current;
    if (!api) return null;
    const ab = await api.save({ selective: true });
    if (!ab) return null;
    const blob = new Blob([ab], { type: session.mime_type });
    await saveDraft({
      session_id: session.session_id,
      doc_id: session.doc_id,
      mime_type: session.mime_type,
      blob,
      base_etag: session.etag,
      dirty_since: Date.now(),
      ai_proposals: [],
    });
    return blob;
  }, [
    session.session_id,
    session.doc_id,
    session.mime_type,
    session.etag,
  ]);

  const scheduleDraft = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void persistDraft();
    }, DRAFT_DEBOUNCE_MS);
  }, [persistDraft]);

  // Flush + upstream save. Wires "Salva" and the imperative `flush()`.
  const flush = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      const blob = await persistDraft();
      if (blob && onSave) await onSave(blob);
      // On success, mark the draft synced so listDirtyDrafts() drops it.
      if (blob) {
        await saveDraft({
          session_id: session.session_id,
          doc_id: session.doc_id,
          mime_type: session.mime_type,
          blob,
          base_etag: session.etag,
          dirty_since: null,
          ai_proposals: [],
        });
      }
      setDirty(false);
    } finally {
      setSaving(false);
    }
  }, [
    onSave,
    persistDraft,
    saving,
    session.doc_id,
    session.etag,
    session.mime_type,
    session.session_id,
  ]);

  // ── Tracked-changes helpers (review-panel-facing) ───────────────────────
  //
  // We expose imperative methods rather than leaking the PM EditorView so
  // the docx primitive stays the single owner of its ProseMirror tree. The
  // review panel calls these to walk and mutate tracked changes without
  // ever holding a reference to PM state. All helpers no-op when the
  // editor hasn't booted yet (returns false / empty array).
  const getPagedView = useCallback(() => {
    const api = editorRef.current;
    if (!api) return null;
    const paged = api.getEditorRef();
    return paged?.getView() ?? null;
  }, []);

  const listTrackedChanges = useCallback((): readonly DocxTrackedChange[] => {
    const view = getPagedView();
    if (!view) return [];
    const state = view.state;
    const out: DocxTrackedChange[] = [];
    // `findNextChange` walks the doc returning the next tracked-change range
    // strictly AFTER `startPos`. We loop from 0 and advance to `range.to`
    // each iteration; an empty range or `null` terminates the walk.
    let cursor = 0;
    const docSize = state.doc.content.size;
    while (cursor < docSize) {
      const range = findNextChange(state, cursor);
      if (!range) break;
      const text = state.doc.textBetween(range.from, range.to, " ", " ").slice(0, 200);
      // Walk up the resolved position to find the enclosing paragraph and
      // its `w14:paraId` attr — used by `scrollToParaId` so the panel can
      // jump the editor to the affected paragraph on click.
      let paraId: string | null = null;
      try {
        const $pos = state.doc.resolve(range.from);
        for (let d = $pos.depth; d >= 0; d -= 1) {
          const node = $pos.node(d);
          const id = (node.attrs as Record<string, unknown>)?.paraId;
          if (typeof id === "string" && id.length > 0) {
            paraId = id;
            break;
          }
        }
      } catch {
        // Out-of-range resolve — leave paraId null, the panel falls back
        // to scrollToPosition(from).
      }
      // Author / timestamp come from the tracked-change mark on the affected
      // node. PM marks are an Object<string,unknown>; we read defensively
      // since the schema may evolve.
      let author: string | null = null;
      let timestamp: string | null = null;
      const node = state.doc.nodeAt(range.from);
      const marks = node?.marks ?? [];
      for (const m of marks) {
        const attrs = m.attrs as Record<string, unknown> | undefined;
        if (!attrs) continue;
        if (typeof attrs.author === "string" && author === null) author = attrs.author;
        if (typeof attrs.timestamp === "string" && timestamp === null) timestamp = attrs.timestamp;
        if (typeof attrs.date === "string" && timestamp === null) timestamp = attrs.date;
      }
      out.push({
        from: range.from,
        to: range.to,
        type: range.type,
        text,
        paraId,
        author,
        timestamp,
      });
      if (range.to <= cursor) break; // safety net against degenerate ranges
      cursor = range.to;
    }
    return out;
  }, [getPagedView]);

  const runCommand = useCallback(
    // `acceptChange` / `rejectChange` / `acceptAllChanges` / `rejectAllChanges`
    // return a PM `Command` shape — `(state, dispatch?) => boolean`. We
    // accept the factory inline rather than typing PM here so this module
    // doesn't depend on `prosemirror-state` types directly (Eigenpal
    // bundles them transitively but the types aren't surfaced).
    (commandFactory: () => ReturnType<typeof acceptChange>): boolean => {
      const view = getPagedView();
      if (!view) return false;
      return commandFactory()(view.state, view.dispatch);
    },
    [getPagedView],
  );

  useImperativeHandle(
    ref,
    () => ({
      flush,
      listTrackedChanges,
      acceptChangeAt: (from, to) => runCommand(() => acceptChange(from, to)),
      rejectChangeAt: (from, to) => runCommand(() => rejectChange(from, to)),
      acceptAllChanges: () => runCommand(() => acceptAllChanges()),
      rejectAllChanges: () => runCommand(() => rejectAllChanges()),
      scrollToPosition: (pos) => {
        const api = editorRef.current;
        api?.scrollToPosition(pos);
      },
      scrollToParaId: (paraId) => {
        const api = editorRef.current;
        return api?.scrollToParaId(paraId) ?? false;
      },
      findInDoc: (query) => {
        const api = editorRef.current;
        if (!api || !query) return [];
        // DocxEditorRef.findInDocument recurses the whole doc (body + table
        // cells + text boxes) and returns one FoundMatch per matching
        // paragraph with its w14:paraId — exactly the anchor proposeChange
        // needs. Cap to keep the seeding loop cheap.
        const matches = api.findInDocument(query, { limit: 8 }) ?? [];
        return matches.map((m) => ({ paraId: m.paraId, match: m.match }));
      },
      subscribeChanges: (listener) => {
        const api = editorRef.current;
        if (!api) return () => {};
        return api.onContentChange(() => listener());
      },
      applyAIProposal(proposal) {
        const api = editorRef.current;
        if (!api) {
          console.warn(
            "[DocxEditorInline] applyAIProposal called before editor ready",
          );
          return false;
        }
        if (proposal.kind !== "docx_replace_range") {
          console.warn(
            `[DocxEditorInline] unsupported proposal kind: ${proposal.kind}`,
          );
          return false;
        }
        // The docx-editor-core agents API exposes `proposeChange` which
        // anchors a tracked change by Word `w14:paraId`. We require the
        // BE to emit proposals in that shape so the suggestion survives
        // unrelated edits. Looser shapes (CFI / char-offset) are deferred
        // to phase 2 once we wire the docx-editor-agents MCP bridge.
        const payload = proposal.payload as {
          paraId?: string;
          search?: string;
          replaceWith?: string;
          author?: string;
        } | null;
        if (
          !payload ||
          typeof payload.paraId !== "string" ||
          typeof payload.search !== "string" ||
          typeof payload.replaceWith !== "string"
        ) {
          console.warn(
            "[DocxEditorInline] docx_replace_range payload missing paraId/search/replaceWith",
          );
          return false;
        }
        return api.proposeChange({
          paraId: payload.paraId,
          search: payload.search,
          replaceWith: payload.replaceWith,
          author: payload.author ?? "Ando",
        });
      },
    }),
    [flush, listTrackedChanges, runCommand],
  );

  // Cleanup the pending debounce on unmount so a unmounted setState never
  // happens (and we don't snapshot stale bytes after the session swaps).
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  // Chrome reduction (May 26, 2026 SOTA pass #2 — user feedback "il titolo
  // appare due volte e non capisco che posso scrivere"). Previously we
  // duplicated the filename: ``FocusedViewLayout``'s rich header surfaced
  // it as ``<h1>``, AND Eigenpal's TitleBar re-rendered it via
  // ``documentName``. We now keep the OUTER eyebrow as the single source
  // of truth and SUPPRESS the inner filename slot via CSS — passing
  // ``documentName=""`` is not enough because Eigenpal falls back to its
  // ``titleBar.untitled`` i18n string and shows "Untitled" as placeholder.
  // The TitleBar itself stays mounted because it hosts (a) the formatting
  // toolbar that drops just below it and (b) our right-side actions
  // (dirty pill + Apri in Word + Salva) injected via ``renderTitleBarRight``.
  //
  // Editability affordance: ProseMirror is in ``mode="editing"`` so the
  // body is genuinely contentEditable, but the surrounding cream gutter
  // didn't communicate that. We add ``cursor: text`` on the gutter wrapper
  // and surface a persistent "Modifica diretta o chiedi ad Ando" hint
  // next to the Salva CTA (subtle, ink-3) — replaces the dirty pill that
  // only appeared AFTER typing.
  return (
    <div ref={shellRef} className={`docx-editor-inline-shell${readOnly ? " docx-editor-inline-preview" : ""} flex h-full min-w-0 w-full flex-col bg-cream`}>
      <style>{INLINE_STYLES}</style>
      <div className="min-h-0 flex-1">
        {loadError ? (
          <div className="px-6 py-10 text-ink-3 text-sm">
            {i18n._(msg`Impossibile caricare il documento.`)} ({loadError.message})
          </div>
        ) : buffer ? (
          <DocxEditor
            ref={editorRef}
            documentBuffer={buffer}
            // Always ``editing`` mode so native tracked-change marks RENDER
            // inline (green insert / red delete). ``viewing`` mode collapses to
            // the clean accepted view and HIDES the redline — wrong for a
            // redline preview. ``readOnly`` blocks typing/save while keeping the
            // revision marks visible (Jun 22, 2026).
            mode="editing"
            readOnly={readOnly}
            author="Ando"
            // We pass an empty string + non-editable so the slot renders
            // nothing useful — the CSS rule above hides whatever residual
            // chrome Eigenpal still emits (e.g. the placeholder span).
            documentName=""
            documentNameEditable={false}
            renderTitleBarRight={() => (
              <div className="flex items-center gap-2">
                {readOnly ? (
                  // Redline preview: no typing / no Save. The user accepts via
                  // the proposal's Conferma (server-side apply). A quiet hint
                  // explains the inline red/green marks.
                  <span className="text-[11px] text-ink-3" aria-live="polite">
                    {i18n._(msg`Anteprima modifiche — conferma per applicare`)}
                  </span>
                ) : dirty ? (
                  <span
                    className="text-[11px] text-ando-orange"
                    aria-live="polite"
                  >
                    {i18n._(msg`Modifiche non salvate`)}
                  </span>
                ) : (
                  // Persistent affordance hint — communicates the dual
                  // editing model (direct typing + AI assistance) the
                  // moment the editor mounts, before the user types.
                  // Hidden on narrow viewports to avoid crowding the
                  // toolbar; the dirty pill takes over once they type.
                  <span className="hidden text-[11px] text-ink-3 lg:inline">
                    {i18n._(msg`Scrivi qui o chiedi ad Ando`)}
                  </span>
                )}
                {openExternal && !readOnly ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openExternal.onClick}
                    disabled={openExternal.busy}
                    title={openExternal.label}
                  >
                    {openExternal.label}
                  </Button>
                ) : null}
                {!readOnly ? (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => void flush()}
                    disabled={saving || !buffer}
                  >
                    {saving ? i18n._(msg`Salvataggio…`) : i18n._(msg`Salva`)}
                  </Button>
                ) : null}
              </div>
            )}
            onChange={() => {
              setDirty(true);
              scheduleDraft();
            }}
            onError={(err) => setLoadError(err)}
          />
        ) : (
          <div className="px-6 py-10 text-ink-3 text-sm">
            {i18n._(msg`Caricamento documento…`)}
          </div>
        )}
      </div>
    </div>
  );
});
